import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Chip } from 'components';
import { useTranslation } from 'react-i18next';
import { toEuros } from 'lib/numbers';
import {
  computeFees,
  computeIncentive,
  computeOptions,
  LazyProductArray,
} from 'lib/priceComputation';
import { OptionalTooltip } from './OptionalTooltip';
import { useMemo } from 'react';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  chipContainer: {
    height: theme.height.chips,
  },
  chipIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase / 2,
  },
}));

interface Props {
  products: LazyProductArray;
  countries?: number;
  quantity?: number;
  duration: number;
  offerName?: string;
  tooltip?: boolean;
  ink?: boolean;
  overGray?: boolean;
}

export const PriceChips = ({
  products,
  countries,
  quantity,
  duration,
  offerName,
  tooltip,
  ink,
  overGray,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { incentive, fees, options } = useMemo(() => {
    const incentive = computeIncentive(products, duration);
    const fees = computeFees(products, duration, incentive, true);
    const options = computeOptions(products, false, duration, incentive);
    return { incentive, fees, options };
  }, [products, duration]);

  return (
    <div className={classes.container}>
      {!!countries && (
        <div className={classes.chipContainer}>
          <Chip color={ink ? 'inkSecondary' : 'blue'} overGray={overGray}>
            <i className={classnames('ri-earth-line', classes.chipIcon)} />
            {countries}
          </Chip>
        </div>
      )}
      {fees > 0 && (
        <OptionalTooltip
          title={t('editSession.price.tooltips.fees', {
            offerName,
          })}
          hide={!offerName || !tooltip}
        >
          <div className={classes.chipContainer}>
            <Chip color={ink ? 'inkSecondary' : 'blue'} overGray={overGray}>
              <i
                className={classnames('ri-price-tag-3-line', classes.chipIcon)}
              />
              {toEuros(fees, true)}
            </Chip>
          </div>
        </OptionalTooltip>
      )}
      {incentive > 0 && (
        <OptionalTooltip
          title={t('editSession.price.tooltips.incentive', { duration })}
          hide={!tooltip}
        >
          <div className={classes.chipContainer}>
            <Chip color={ink ? 'inkSecondary' : 'yellow'} overGray={overGray}>
              <i className={classnames('ri-gift-line', classes.chipIcon)} />
              {toEuros(incentive, true)}
            </Chip>
          </div>
        </OptionalTooltip>
      )}
      {!!quantity && (
        <OptionalTooltip
          title={t('editSession.price.tooltips.participants')}
          hide={!tooltip}
        >
          <div className={classes.chipContainer}>
            <Chip color="inkSecondary">
              <i className={classnames('ri-user-3-line', classes.chipIcon)} />
              {quantity}
            </Chip>
          </div>
        </OptionalTooltip>
      )}
      {options > 0 && (
        <OptionalTooltip
          title={t('editSession.price.tooltips.fixedPrices')}
          hide={!tooltip}
        >
          <div className={classes.chipContainer}>
            <Chip color={ink ? 'inkSecondary' : 'red'} overGray={overGray}>
              <i
                className={classnames('ri-add-circle-line', classes.chipIcon)}
              />
              {toEuros(options, true)}
            </Chip>
          </div>
        </OptionalTooltip>
      )}
    </div>
  );
};
